import { useRef, useState } from "react";
import { X, UploadCloud, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useSheets } from "../modal/SheetsContext";
import type { CsvImportPreview } from "../api/SheetsApi";

interface Props {
  onClose: () => void;
}

export default function CsvImportModal({ onClose }: Props) {
  const { importCsvPreview, importCsvCommit, columns, rows } = useSheets();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CsvImportPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // A sheet with no rows yet has nothing to lose — the backend will replace
  // its whole column set with the imported file's own columns. That's a
  // real deletion of any customized columns, so it's confirmed up front,
  // before even running the preview, rather than buried in the preview step.
  const willReplaceColumns = rows.length === 0 && columns.length > 0;

  async function handleFileSelect(f: File) {
    if (willReplaceColumns) {
      const names = columns.map((c) => c.label).join(", ");
      const ok = window.confirm(
        `This sheet has no rows yet, so importing will replace its ${columns.length} existing column(s) (${names}) with the columns from this file. Continue?`
      );
      if (!ok) {
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }
    setFile(f);
    setPreview(null);
    setError(null);
    setLoadingPreview(true);
    try {
      setPreview(await importCsvPreview(f));
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to read file");
    } finally {
      setLoadingPreview(false);
    }
  }

  async function confirmImport() {
    if (!file) return;
    setImporting(true);
    setError(null);
    try {
      await importCsvCommit(file);
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to import file");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <UploadCloud className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">Import CSV / Excel</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-emerald-400 font-inter bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-3">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              Imported {preview?.row_count ?? 0} rows.
            </div>
            {preview && preview.skipped_headers.length > 0 && (
              <div className="flex items-start gap-2 text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>Only an admin can create new columns, so these weren't imported: {preview.skipped_headers.join(", ")}</span>
              </div>
            )}
          </div>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xlsm"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full border border-dashed border-border rounded-xl px-4 py-6 text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors font-inter mb-4"
            >
              {file ? file.name : "Click to choose a .csv or .xlsx file"}
            </button>

            {loadingPreview && <p className="text-xs text-muted-foreground font-inter mb-4">Reading file…</p>}

            {preview && (
              <div className="space-y-2 mb-4">
                <p className="text-xs text-foreground font-inter">{preview.row_count} row(s) found.</p>
                {preview.matched_headers.length > 0 && (
                  <p className="text-xs text-muted-foreground font-inter">
                    Matched columns: {preview.matched_headers.join(", ")}
                  </p>
                )}
                {preview.new_columns.length > 0 && (
                  <div className="flex items-start gap-2 text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>These columns don't exist yet and will be created: {preview.new_columns.join(", ")}</span>
                  </div>
                )}
                {preview.removed_columns.length > 0 && (
                  <div className="flex items-start gap-2 text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>These unused default columns will be removed to make room: {preview.removed_columns.join(", ")}</span>
                  </div>
                )}
                {preview.skipped_headers.length > 0 && (
                  <div className="flex items-start gap-2 text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Only an admin can create new columns, so these columns won't be imported: {preview.skipped_headers.join(", ")}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {error && (
          <p className="text-xs text-destructive font-inter mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors font-inter"
          >
            {done ? "Close" : "Cancel"}
          </button>
          {!done && (
            <button
              onClick={confirmImport}
              disabled={!preview || importing || preview.row_count === 0}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-inter"
            >
              {importing ? "Importing…" : "Confirm Import"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
